import { useState } from "react";
import { ShoppingCart, Heart } from "lucide-react";
import { formatPrice, BRAND_COLORS, BRAND_LOGOS, CSS_TEXT_LOGOS } from "./shopData";
import { formatCarModel } from "./formatCarModel";

export default function ProductCard({ product, onAdd, onView, onToggleWishlist, inWishlist }) {
  const [imgFailed, setImgFailed] = useState(false);
  const brandColor = BRAND_COLORS[product.brand] || { bg: "#1a1a1a", text: "#FF3B30" };
  const cssLogo = CSS_TEXT_LOGOS[product.brand];
  const stockQty = product.available;
  const hasStock = stockQty !== undefined && stockQty !== null;

  return (
    <div
      data-testid={`product-card-${product.slug}`}
      className="group bg-[#111] border border-[#1E1E1E] rounded-xl overflow-hidden hover:border-[#FF3B30]/40 transition-all duration-300 cursor-pointer hover:shadow-[0_0_30px_rgba(255,59,48,0.06)]"
      onClick={() => onView(product)}
    >
      <div className="aspect-square bg-white flex items-center justify-center overflow-hidden relative">
        {onToggleWishlist && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onToggleWishlist(product); }}
            className={`absolute top-2 right-2 z-10 w-8 h-8 rounded-full flex items-center justify-center transition-all backdrop-blur-sm ${inWishlist ? "bg-[#FF3B30] text-white shadow-lg" : "bg-black/40 text-white hover:bg-[#FF3B30]"}`}
            data-testid={`wishlist-toggle-${product.slug}`}
            title={inWishlist ? "Verwijder van verlanglijst" : "Voeg toe aan verlanglijst"}
            aria-label={inWishlist ? "Verwijder van verlanglijst" : "Voeg toe aan verlanglijst"}
          >
            <Heart size={15} fill={inWishlist ? "currentColor" : "none"} />
          </button>
        )}
        {product.image_url && !imgFailed ? (
          <img
            src={product.image_url}
            alt={product.title}
            className="w-full h-full object-contain p-3 group-hover:scale-[1.03] transition-transform duration-500"
            loading="lazy"
            onError={() => setImgFailed(true)}
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center p-4" style={{ background: brandColor.bg }}>
            {cssLogo ? (
              <span className="font-bold text-xl opacity-60" style={{ color: cssLogo.color, fontFamily: cssLogo.font }}>{cssLogo.text}</span>
            ) : (
              <span className="font-['Orbitron'] font-bold text-sm text-center leading-tight" style={{ color: brandColor.text }}>{product.brand}</span>
            )}
            <span className="text-[10px] text-[#555] mt-2 text-center leading-tight line-clamp-2">{product.manufacturer_item || ""}</span>
          </div>
        )}
        <span className="absolute top-1.5 left-1.5 text-[8px] font-bold uppercase tracking-widest bg-[#FF3B30] text-white px-1.5 py-0.5 rounded">{product.brand}</span>
        {product.product_category && (
          <span className="absolute bottom-1.5 left-1.5 text-[8px] bg-black/60 text-white/80 px-1.5 py-0.5 rounded">{product.product_category}</span>
        )}
        {hasStock && (
          <span className={`absolute top-1.5 right-1.5 text-[8px] font-semibold px-1.5 py-0.5 rounded ${stockQty > 0 ? 'bg-[#27AE60]/90 text-white' : 'bg-[#555]/80 text-[#ccc]'}`}>
            {stockQty > 0 ? `${stockQty} op voorraad` : "Op bestelling"}
          </span>
        )}
      </div>
      <div className="p-3">
        <h3 className="text-white text-[11px] sm:text-[12px] font-medium leading-snug line-clamp-2 min-h-[2.2rem] mb-1">{product.title}</h3>
        {product.matched_application ? (
          <p className="text-[#F1C40F] text-[10px] truncate mb-1" title={`Past ook op ${product.car_make || ''} ${product.car_model || ''}`.trim()}>
            Past op {product.matched_application.make}{product.matched_application.model ? ` ${formatCarModel(product.matched_application.model, product.matched_application.make).display}` : ""}
          </p>
        ) : product.car_make && (
          <p className="text-[#666] text-[10px] truncate mb-1" title={`${product.car_make}${product.car_model ? ' ' + product.car_model : ''}`}>
            {product.car_make}{product.car_model ? ` ${formatCarModel(product.car_model, product.car_make).display}` : ""}
          </p>
        )}
        <div className="flex items-center justify-between mt-1.5">
          <span data-testid="product-price" className="text-[#FF3B30] font-bold text-sm">{formatPrice(product.price)}</span>
          <button
            data-testid={`add-to-cart-${product.slug}`}
            onClick={(e) => { e.stopPropagation(); onAdd(product); }}
            className="w-8 h-8 rounded-lg bg-[#FF3B30]/10 flex items-center justify-center text-[#FF3B30] hover:bg-[#FF3B30] hover:text-white transition-all"
          >
            <ShoppingCart size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
